import { Capacitor, registerPlugin } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { MAX_IMPORT_BYTES } from '../domain/model';
import { downloadTextFile } from './backup';
import { householdExportDirectory } from './household-artifacts';

const ALLOWED_TEXT_TYPES = new Set([
  'application/json;charset=utf-8',
  'text/csv;charset=utf-8',
]);
const SAFE_FILENAME = /^[a-z0-9][a-z0-9._-]{0,119}$/i;
const LocalDocument = registerPlugin<{
  save(options: { filename: string; content: string; mimeType: string }): Promise<void>;
}>('LocalDocument');

export const validateTextExport = (filename: string, content: string, type: string): void => {
  if (!SAFE_FILENAME.test(filename) || filename.includes('..')) {
    throw new Error('Nom de fichier d’export non autorisé.');
  }
  if (!ALLOWED_TEXT_TYPES.has(type)) {
    throw new Error('Type de fichier d’export non autorisé.');
  }
  if (new TextEncoder().encode(content).byteLength > MAX_IMPORT_BYTES) {
    throw new Error('L’export dépasse la limite locale de 5 Mo.');
  }
};

export const exportTextFile = async (
  householdId: string,
  filename: string,
  content: string,
  type: string,
): Promise<'downloaded' | 'shared'> => {
  validateTextExport(filename, content, type);
  if (!Capacitor.isNativePlatform()) {
    downloadTextFile(filename, content, type);
    return 'downloaded';
  }

  if (Capacitor.getPlatform() === 'android') {
    await LocalDocument.save({
      filename, content,
      mimeType: type === 'application/json;charset=utf-8' ? 'application/json' : 'text/csv',
    });
    return 'downloaded';
  }

  const shareCapability = await Share.canShare();
  if (!shareCapability.value) {
    throw new Error('Le partage de fichier n’est pas disponible sur cet appareil.');
  }

  const path = `${householdExportDirectory(householdId)}/${filename}`;
  const written = await Filesystem.writeFile({
    path,
    data: content,
    directory: Directory.Cache,
    encoding: Encoding.UTF8,
    recursive: true,
  });
  try {
    await Share.share({
      title: filename.endsWith('.json') ? 'Sauvegarde Frozen Save' : 'Export Frozen Save',
      dialogTitle: 'Enregistrer ou partager l’export local',
      url: written.uri,
    });
    return 'shared';
  } finally {
    await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => {
      // Le cache reste borné au fichier courant si la plateforme l’a déjà supprimé.
    });
  }
};
